import type {
  BookingCalendarDTO,
  BookingDetailDTO,
  BookingDTO,
  BulkScheduleRequestDTO,
  BulkScheduleResponseDTO,
  CheckActivePrivateLessonPackageResponseDTO,
  CheckAvailabilityRequestDTO,
  CheckAvailabilityResponseDTO,
  CreateBookingDTO,
  CreatePrivateLessonTemplateDTO,
  PrivateLessonTemplateDTO,
  SellPrivateLessonGroupPackageDTO,
  SellPrivateLessonPrivateSharedPackageDTO,
  SellPrivateLessonPackageDTO,
  SellPrivateLessonPackageResponseDTO,
  UpdateBookingDTO,
  UpdatePrivateLessonTemplateDTO,
} from "@/modules/private-lessons/domain/contracts";
import type { PrivateLessonPackageDetailDTO } from "@/modules/private-lessons/domain/repo/get-package-by-id";
import type { PrivateLessonPackageDTO } from "@/modules/private-lessons/domain/repo/list-packages";
import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { PrivateLessonTemplateUsageStats } from "../../domain/repo/get-usage-stats";

export const privateLessonsApi = {
  templates: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<
      OffsetPageDTO<PrivateLessonTemplateDTO & { packagesCount: number }>
    > {
      const url = new URL(`${config.apiUrl}/private-lessons/templates`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<
        OffsetPageDTO<PrivateLessonTemplateDTO & { packagesCount: number }>
      >(url.toString());
    },
    async get(id: string): Promise<PrivateLessonTemplateDTO> {
      return fetcher.get<PrivateLessonTemplateDTO>(
        `${config.apiUrl}/private-lessons/templates/${id}`,
      );
    },
    async getUsageStats(id: string): Promise<PrivateLessonTemplateUsageStats> {
      return fetcher.get(
        `${config.apiUrl}/private-lessons/templates/${id}/usage-stats`,
      );
    },
    async create(
      input: CreatePrivateLessonTemplateDTO,
    ): Promise<{ id: string }> {
      return fetcher.post<{ id: string }>(
        `${config.apiUrl}/private-lessons/templates`,
        input,
      );
    },
    async update(
      id: string,
      input: UpdatePrivateLessonTemplateDTO,
    ): Promise<{ id: string }> {
      return fetcher.put<{ id: string }>(
        `${config.apiUrl}/private-lessons/templates/${id}`,
        input,
      );
    },
    async toggleStatus(
      id: string,
      status: "active" | "inactive",
    ): Promise<{ toggled: boolean }> {
      return fetcher.post<{ toggled: boolean }>(
        `${config.apiUrl}/private-lessons/templates/${id}/toggle-status`,
        { status },
      );
    },
    async remove(id: string): Promise<{ id: string }> {
      return fetcher.delete<{ id: string }>(
        `${config.apiUrl}/private-lessons/templates/${id}`,
      );
    },
    async bulkDelete(input: {
      ids: string[];
    }): Promise<{ deletedCount: number }> {
      return fetcher.post<{ deletedCount: number }>(
        `${config.apiUrl}/private-lessons/templates/bulk-delete`,
        input,
      );
    },
    async bulkToggleStatus(input: {
      ids: string[];
      status: "active" | "inactive";
    }): Promise<{ updatedCount: number }> {
      return fetcher.post<{ updatedCount: number }>(
        `${config.apiUrl}/private-lessons/templates/bulk-toggle-status`,
        input,
      );
    },
  },
  sell: {
    async checkActivePackage(
      studentId: string,
    ): Promise<CheckActivePrivateLessonPackageResponseDTO> {
      return fetcher.get<CheckActivePrivateLessonPackageResponseDTO>(
        `${config.apiUrl}/private-lessons/sell/check-active-package/${studentId}`,
      );
    },
    async sellPackage(
      input: SellPrivateLessonPackageDTO,
    ): Promise<SellPrivateLessonPackageResponseDTO> {
      return fetcher.post<SellPrivateLessonPackageResponseDTO>(
        `${config.apiUrl}/private-lessons/sell`,
        input,
      );
    },
    async sellGroupPackage(
      input: SellPrivateLessonGroupPackageDTO,
    ): Promise<SellPrivateLessonPackageResponseDTO> {
      return fetcher.post<SellPrivateLessonPackageResponseDTO>(
        `${config.apiUrl}/private-lessons/sell/group`,
        input,
      );
    },
    async sellPrivateSharedPackage(
      input: SellPrivateLessonPrivateSharedPackageDTO,
    ): Promise<SellPrivateLessonPackageResponseDTO> {
      return fetcher.post<SellPrivateLessonPackageResponseDTO>(
        `${config.apiUrl}/private-lessons/sell/private-shared`,
        input,
      );
    },
  },
  packages: {
    list: (
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<PrivateLessonPackageDTO>> => {
      const url = new URL(`${config.apiUrl}/private-lessons/packages`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<PrivateLessonPackageDTO>>(
        url.toString(),
      );
    },
    async getById(id: string): Promise<PrivateLessonPackageDetailDTO> {
      return fetcher.get<PrivateLessonPackageDetailDTO>(
        `${config.apiUrl}/private-lessons/packages/${id}`,
      );
    },
    async cancel(
      id: string,
      body: { cancelReason: string },
    ): Promise<{ success: boolean }> {
      return fetcher.put<{ success: boolean }>(
        `${config.apiUrl}/private-lessons/packages/${id}/cancel`,
        body,
      );
    },
  },
  bookings: {
    async checkAvailability(
      input: CheckAvailabilityRequestDTO,
    ): Promise<CheckAvailabilityResponseDTO> {
      return fetcher.post<CheckAvailabilityResponseDTO>(
        `${config.apiUrl}/private-lessons/bookings/check-availability`,
        input,
      );
    },
    async bulkSchedule(
      input: BulkScheduleRequestDTO,
    ): Promise<BulkScheduleResponseDTO> {
      return fetcher.post<BulkScheduleResponseDTO>(
        `${config.apiUrl}/private-lessons/bookings/bulk-schedule`,
        input,
      );
    },
    list: (query: OffsetPageQueryDTO): Promise<OffsetPageDTO<BookingDTO>> => {
      const url = new URL(`${config.apiUrl}/private-lessons/bookings`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<BookingDTO>>(url.toString());
    },
    getCalendar: (
      start: string,
      end: string,
      filters?: {
        roomId?: string;
        instructorId?: string;
        status?: string;
      },
    ): Promise<{ data: BookingCalendarDTO[] }> => {
      const url = new URL(`${config.apiUrl}/private-lessons/bookings/calendar`);
      url.searchParams.set("start", start);
      url.searchParams.set("end", end);
      if (filters?.roomId) url.searchParams.set("roomId", filters.roomId);
      if (filters?.instructorId)
        url.searchParams.set("instructorId", filters.instructorId);
      if (filters?.status) url.searchParams.set("status", filters.status);
      return fetcher.get<{ data: BookingCalendarDTO[] }>(url.toString());
    },
    getById: (id: string): Promise<BookingDetailDTO> =>
      fetcher.get<BookingDetailDTO>(
        `${config.apiUrl}/private-lessons/bookings/${id}`,
      ),
    create: (input: CreateBookingDTO): Promise<{ id: string }> =>
      fetcher.post<{ id: string }>(
        `${config.apiUrl}/private-lessons/bookings`,
        input,
      ),
    update: (
      id: string,
      input: UpdateBookingDTO,
    ): Promise<{ success: boolean }> =>
      fetcher.put<{ success: boolean }>(
        `${config.apiUrl}/private-lessons/bookings/${id}`,
        input,
      ),
    cancel: (id: string): Promise<{ success: boolean }> =>
      fetcher.put<{ success: boolean }>(
        `${config.apiUrl}/private-lessons/bookings/${id}/cancel`,
        {},
      ),
    checkIn: (
      id: string,
      body?: {
        instructorIds?: string[];
        memberStudentIds?: string[];
      },
    ): Promise<{
      attendanceId: string;
      attendanceIds?: string[];
      consumptionId: string | null;
    }> =>
      fetcher.post<{
        attendanceId: string;
        attendanceIds?: string[];
        consumptionId: string | null;
      }>(`${config.apiUrl}/private-lessons/bookings/${id}/check-in`, body ?? {}),
    complete: (id: string): Promise<{ id: string }> =>
      fetcher.post<{ id: string }>(
        `${config.apiUrl}/private-lessons/bookings/${id}/complete`,
        {},
      ),
    getAttendance: (
      id: string,
    ): Promise<{
      records: Array<{
        id: string;
        studentId: string;
        studentName: string;
        studentMemberId: string;
        checkedInAt: string;
        checkedInBy: string;
        overrideReason: string | null;
        overrideBy: string | null;
        createdAt: string;
      }>;
    }> =>
      fetcher.get<{
        records: Array<{
          id: string;
          studentId: string;
          studentName: string;
          studentMemberId: string;
          checkedInAt: string;
          checkedInBy: string;
          overrideReason: string | null;
          overrideBy: string | null;
          createdAt: string;
        }>;
      }>(`${config.apiUrl}/private-lessons/bookings/${id}/attendance`),
    getInstructorAttendees: (
      id: string,
    ): Promise<{
      attendees: Array<{
        id: string;
        instructorId: string;
        instructorName: string;
        checkedInAt: string;
        isSubstitute: boolean | null;
      }>;
    }> =>
      fetcher.get<{
        attendees: Array<{
          id: string;
          instructorId: string;
          instructorName: string;
          checkedInAt: string;
          isSubstitute: boolean | null;
        }>;
      }>(
        `${config.apiUrl}/private-lessons/bookings/${id}/instructor-attendees`,
      ),
    correctInstructorCheckIn: (
      id: string,
      body: {
        wrongCheckInId: string;
        correctInstructorId: string;
        reason: string;
      },
    ): Promise<{
      removedCheckInId: string;
      removedInstructorId: string;
      removedInstructorName: string;
      newCheckInId: string;
      correctInstructorId: string;
      correctInstructorName: string;
    }> =>
      fetcher.post(
        `${config.apiUrl}/private-lessons/bookings/${id}/correct-instructor-check-in`,
        body,
      ),
  },
};
